"use client";

import Image from "next/image";
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from "@headlessui/react";
import { forwardRef, useState, type JSX } from "react";
import { getThemeClassName } from "@/theming/selectors";
import classNames from "classnames";
import { PiCaretDown } from "react-icons/pi";
import { ProductVariantList } from "@/cms/types/objects";
import { Themes } from "@/theming/types";
import { PiCheck, PiInfo, PiX } from "react-icons/pi";
import { Price } from "@/products/pricing";

export type SelectorItem = {
  key: string;
  title: string | number;
  subTitle: string | number;
  image?: { src?: string | null; alt: string; aspectRatio?: number };
  slug?: string | null;
  disabled?: boolean;
  price?: Price;
  additionalInfo?: string;
};

type ProductVariantSelectProps = {
  subscription?: boolean;
  theme?: Themes;
  listboxButtonClassName?: string;
} & ProductVariantList;

export const ProductVariantSelect = forwardRef<
  HTMLDivElement,
  ProductVariantSelectProps & {
    value?: SelectorItem;
    onChange?: (value: SelectorItem) => void;
    onClose?: () => void;
    id?: string;
  } & Pick<JSX.IntrinsicElements["div"], "className">
>(
  (
    {
      items,
      className,
      theme,
      value,
      listboxButtonClassName,
      onChange,
      onClose,
      id,
    },
    ref,
  ) => {
    const [query, setQuery] = useState("");
    const selectedValue = value ?? items?.[0];
    const hasMoreThanOneFormat = (items?.length || 0) > 1;

    const filteredItems =
      query === ""
        ? items || []
        : (items || []).filter(
            (item) =>
              item.title
                .toString()
                .toLowerCase()
                .includes(query.toLowerCase()) ||
              item.subTitle
                .toString()
                .toLowerCase()
                .includes(query.toLowerCase()),
          );

    const handleItemClick = (item: SelectorItem) => {
      if (!onChange) {
        return;
      }
      onChange(item);
    };

    const showSearchInput = items.length > 20;

    return (
      <div ref={ref} className={classNames("relative mt-2 w-full", className)}>
        <Combobox
          value={selectedValue}
          onChange={(newValue: SelectorItem | null) => {
            if (newValue) {
              handleItemClick(newValue);
            }
          }}
          onClose={() => {
            setQuery("");
            onClose?.();
          }}
        >
          {({ open }) => (
            <div>
              <ComboboxButton
                className={classNames(
                  `grid max-h-80 w-full cursor-auto gap-4 cvr-card rounded-xl bg-white px-4 py-2 ${
                    hasMoreThanOneFormat && "cursor-pointer"
                  }`,
                  listboxButtonClassName,
                  getThemeClassName(theme),
                )}
                id={id}
              >
                <div className="flex items-center justify-between gap-3 text-black">
                  <div className="flex gap-3">
                    <div>
                      {selectedValue?.image?.src && (
                        <Image
                          src={selectedValue.image.src}
                          height={70}
                          width={Math.round(
                            70 * (selectedValue.image.aspectRatio ?? 4 / 3),
                          )}
                          alt={selectedValue.image.alt}
                        />
                      )}
                    </div>
                    <span className="grid self-center text-left">
                      <span className="font-semibold">
                        {selectedValue?.title}
                      </span>
                      <span className="text-sm text-gray-800">
                        {selectedValue?.subTitle}
                      </span>
                    </span>
                  </div>
                  <div>
                    {hasMoreThanOneFormat && (
                      <span className="flex w-full items-center gap-2 text-left">
                        <PiCaretDown size={24} className="my-3 self-center" />
                      </span>
                    )}
                  </div>
                </div>
              </ComboboxButton>
              {open && (
                <div className="fixed inset-0 z-50 m-0! overflow-y-auto bg-black/80">
                  <div className="fixed inset-x-0 inset-y-80 bottom-0 m-2 max-h-[80vh] overflow-y-auto rounded-xl bg-white shadow-lg md:m-4">
                    <div className="p-4 sm:p-8">
                      <div className="mb-4 flex items-center justify-between">
                        <span className="text-center font-medium text-elevated-contrast">
                          Velg første bok
                        </span>
                        <span className="rounded text-gray-800">
                          <PiX className="inline" size={25} />
                        </span>
                      </div>
                      {showSearchInput && (
                        <div className="mb-4">
                          <ComboboxInput
                            className="cvr-control w-full"
                            placeholder="Søk etter bok..."
                            displayValue={() => query}
                            onChange={(event) => setQuery(event.target.value)}
                          />
                        </div>
                      )}
                      <ComboboxOptions as="ul" className="grid gap-4" static>
                        {filteredItems.map((item) => (
                          <ComboboxOption
                            key={item.key}
                            className={classNames(
                              "flex items-center rounded-xl bg-white",
                              "px-3 py-3 md:px-6",
                              "hover:cursor-pointer hover:bg-[#F2EFF0]",
                              "data-selected:bg-[#C9F2BF]",
                              "data-disabled:bg-white! data-disabled:opacity-50 data-disabled:hover:cursor-not-allowed",
                            )}
                            value={item}
                            onClick={() => handleItemClick(item)}
                            disabled={item.disabled}
                            aria-labelledby={item.key}
                          >
                            {({ selected }) => (
                              <div className="flex w-full items-center justify-between">
                                <div className="flex items-center gap-3">
                                  {item.image?.src && (
                                    <Image
                                      src={item.image.src}
                                      height={70}
                                      width={Math.round(
                                        70 * (item.image.aspectRatio ?? 4 / 3),
                                      )}
                                      alt={item.image.alt}
                                    />
                                  )}
                                  <span
                                    id={`${item.key}`}
                                    className="grid self-center text-left"
                                  >
                                    <span className="font-semibold text-elevated-contrast">
                                      {item.title}
                                    </span>
                                    <span className="text-sm text-gray-800">
                                      {item.subTitle}
                                    </span>
                                    {item.additionalInfo && (
                                      <span className="flex items-center gap-1 text-xs text-gray-600">
                                        <PiInfo
                                          size={16}
                                          className="min-w-[16px]"
                                        />
                                        {item.additionalInfo}
                                      </span>
                                    )}
                                  </span>
                                </div>
                                {selected && (
                                  <PiCheck size={24} className="self-center" />
                                )}
                              </div>
                            )}
                          </ComboboxOption>
                        ))}
                      </ComboboxOptions>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </Combobox>
      </div>
    );
  },
);

ProductVariantSelect.displayName = "ProductVariantSelect";
